import * as PIXI from "pixi.js";
import anime from "animejs";
import { symbolData } from "../../../SymbolData";
import {
  formatToCustomLocale,
} from "../../../utilities";
import {
  Settings,
} from "../../../settings";
import {
  COIN_SYMBOL_IDS,
  COIN_GENERATION_PROBABILITY,
} from "../../../SymbolData";
import { FEATURES_CONFIG } from "../../../featuresConfig";
import { setSpinComplete, incrementWindupCount } from "../../../State/actions";
import { soundManager } from "../../../SoundManager";
import { SOUND_NAME } from "../../../sounds";
import store from "../../../State/Store/store";

// Dynamic sprite count formula: 1 top buffer + N visible + N landing zone + 1 bottom buffer
const SPRITE_COUNT = Settings.SYMBOLS_VISIBLE * 2 + 2;
const VISIBLE_SYMBOL_COUNT = Settings.SYMBOLS_VISIBLE;
// Visible symbols start after top buffer (index 1) + landing zone (N symbols)
const VISIBLE_SYMBOL_START_INDEX = 1 + Settings.SYMBOLS_VISIBLE;
// Stop animation moves sprites down by the landing zone distance (N symbol heights)
const STOP_MOVE_DISTANCE_SYMBOLS = Settings.SYMBOLS_VISIBLE;

// Cache available symbol IDs once (module-level constant for O(1) performance)
// Dynamically reads all symbols from symbolData that have a texture
const AVAILABLE_SYMBOL_IDS = (() => {
  return Object.entries(symbolData)
    .filter(([id, data]) => data?.texture && data.texture !== null)
    .map(([id]) => parseInt(id, 10));
})();

// Safari-specific precision factor (Safari doesn't need as much precision)
const PRECISION_FACTOR = /^((?!chrome|android).)*safari/i.test(
  navigator.userAgent
)
  ? 10000
  : 100000;

// Main game reel animation controller - handles wind-up, spin, stop animations with sprite wrapping
class ReelControllerV2 {
  constructor(
    dispatch,
    spinningContainer,
    spriteSheet,
    spriteSheetBlurred,
    // multiplierSpriteSheet, // TODO: Add multiplier support later
    reels,
    reelIndex,
    symbolWidth,
    symbolHeight,
    onReelStop = null, // Callback when reel stops (for landing animations)
    autoPlaySpeed = "auto", // Current autoplay speed mode
    bet = 1 // Current bet value for coin display
  ) {
    this.dispatch = dispatch;
    this.spinningContainer = spinningContainer;
    this.reelIndex = reelIndex;
    this.spritesheet = spriteSheet;
    this.spritesheetBlurred = spriteSheetBlurred;
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.reels = reels || []; // Store API landing data (array of symbol IDs)
    this.hasPendingLandingSymbols = false; // Flag to indicate API data is ready
    this.onReelStop = onReelStop; // Callback for landing animations
    this.onReelLanded = null; // Callback when reel has fully landed (bounce complete, after onReelStop)
    this.autoPlaySpeed = autoPlaySpeed; // Current speed setting
    this.lockedSpinSpeed = null; // Locked speed for current spin (prevents mid-spin changes)
    this.bet = bet || 1; // Store bet value for coin calculations

    // Animation state
    this.isPlaying = false;
    this.hasStopped = false;
    this.isStopping = false;
    this.isTensionMode = false;
    this.tickerCallback = null;
    this.spinningSprites = [];
    this.spinStartTime = null;
    this.normalizedSpinSpeed = 0;
    this.lastCalculatedPosition = 0;
    this.pendingLandingSymbolId = null; // Deprecated: use this.reels array instead
    this.isBlurred = false;
    this.visibleSprites = [];
    this.nativeDimensions = null;
    this.hasInitializedTextures = false;
    this.stopTweenActive = false;
    this._pendingPositionUpdate = false;
    this._originalSymbolHeight = symbolHeight;
    this._targetSymbolHeight = symbolHeight;
    this.activeStopAnimation = null;
    this.stopAnimationProgress = 0;
    this.isWindingUp = false;
    this.windUpAnimation = null;
    this.speedMultiplier = 1.0;
    this.hasWinThisSpin = false; // Track if this spin has a win (affects sound playback)
    this.isHoldAndWinTrigger = false; // Track if this spin triggers Hold & Win (6+ coins)
    this.pendingTimeouts = []; // Track setTimeout calls for cleanup
    this.isExpandingWildActive = false; // Lock flag to prevent texture updates during expanding wild animation

    // Premium feel: acceleration, deceleration, velocity-based blur
    this.accelerationMultiplier = 1.0; // Ramps 0→1 during spin start
    this.currentVelocity = 0; // Effective velocity as fraction of max (0-1), used for blur
    this.isDecelerating = false; // True during phase 1 of 2-phase stop
    this.decelerationStartTime = null; // performance.now() when deceleration began
    this.decelerationStartSpeed = 1.0; // Speed multiplier at deceleration start

    // Get symbol data from spritesheet
    this.reelSymbolsData =
      spriteSheet?.data?.frames || spriteSheet?.textures || null;

    // Internal caches for performance optimization
    this._speedConfigCache = null;
    this._lastLockedSpeed = null;
    this._lastAutoPlaySpeed = null;
    this._nativeDimensionsCache = new Map(); // Cache per symbol name
    this._wrapThresholdCache = null;
    this._lastSymbolHeightForWrapThreshold = null;

    this._createSpinningSprites();
    this.spinningContainer.roundPixels = true;
  }

  // Picks a random symbol from available symbols (O(1) direct random)
  _pickRandomSymbolId() {
    if (!AVAILABLE_SYMBOL_IDS.length) {
      return 1; // fallback
    }
    return AVAILABLE_SYMBOL_IDS[
      Math.floor(Math.random() * AVAILABLE_SYMBOL_IDS.length)
    ];
  }

  // Calculates display value for coin symbols (coinValue * bet) with locale formatting
  _getCoinDisplayValue(symbolValue) {
    if (!symbolValue || symbolValue === null) {
      return "";
    }

    try {
      const coinValue = parseInt(symbolValue, 10);

      if (isNaN(coinValue) || coinValue <= 0) {
        return "";
      }

      // Use free rounds bet when active, otherwise use regular bet
      const state = store.getState();
      const freeRoundsActive = state?.freeRounds?.status === "active";
      const freeRoundsBet = state?.freeRounds?.betValue || null;
      const bet = freeRoundsActive && freeRoundsBet ? freeRoundsBet : this.bet;

      const displayValue = coinValue * bet;
      return formatToCustomLocale(displayValue);
    } catch (error) {
      return "";
    }
  }

  // Receives new reel data from API - used for landing symbols when reel stops
  updateReels(newReels) {
    if (newReels && Array.isArray(newReels)) {
      this.reels = newReels;
      this.hasPendingLandingSymbols = true;
    }
  }

  // Updates bet value - coin texts will refresh naturally during spin when symbols wrap
  updateBet(newBet) {
    this.bet = newBet || 1;
    // Note: Coin texts update automatically when symbols wrap during spin via _setTexture()
    // This prevents immediate texture updates and maintains smooth gameplay
  }

  // Refresh all visible coin value texts (used when bet changes during free rounds)
  _refreshVisibleCoinTexts() {
    if (!this.spinningSprites || this.spinningSprites.length === 0) return;

    // Refresh ALL spinning sprites (not just visibleSprites which may not be set yet)
    for (let i = 0; i < this.spinningSprites.length; i++) {
      const spriteData = this.spinningSprites[i];
      if (!spriteData || !spriteData.valueText || !spriteData.currentSymbolId) continue;

      const symbolInfo = symbolData[spriteData.currentSymbolId];
      if (!symbolInfo || !symbolInfo.value) continue;

      // Recalculate coin display value with new bet
      const displayValue = this._getCoinDisplayValue(symbolInfo.value);
      if (displayValue && displayValue !== "") {
        spriteData.valueText.text = displayValue;
        if (spriteData.valueTextBlurred) {
          spriteData.valueTextBlurred.text = displayValue;
        }
      }
    }
  }

  // Updates texture for spinning sprites
  updateSymbolTexture(symbolId) {
    const symbolInfo = symbolData[symbolId];
    if (!symbolInfo || this.spinningSprites.length === 0) return;

    const visibleSpriteIndex = this.spinningSprites.length - 1;
    this._setSpriteTexture(this.spinningSprites[visibleSpriteIndex], symbolId);
    this.symbolId = symbolId;
  }

  // Creates spinning sprites with wrapping
  _createSpinningSprites() {
    if (!this.spinningContainer) return;

    for (let i = 0; i < SPRITE_COUNT; i++) {
      const symbolContainer = new PIXI.Container();
      symbolContainer.y = i * this.symbolHeight;
      symbolContainer.roundPixels = true;

      const normalSprite = new PIXI.Sprite();
      normalSprite.anchor.set(0.5);
      normalSprite.name = "spinning_symbol_texture";
      normalSprite.alpha = 1;
      normalSprite.roundPixels = true;
      symbolContainer.addChild(normalSprite);

      const blurredSprite = new PIXI.Sprite();
      blurredSprite.anchor.set(0.5);
      blurredSprite.name = "spinning_symbol_texture_blurred";
      blurredSprite.alpha = 0;
      blurredSprite.roundPixels = true;
      symbolContainer.addChild(blurredSprite);

      let valueText = null;
      let valueTextBlurred = null;
      if (FEATURES_CONFIG.HOLD_AND_WIN.ENABLED) {
        const fontSize = this.symbolHeight * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER;
        valueText = new PIXI.BitmapText("", {
          fontName: "font",
          fontSize: fontSize,
        });
        valueText.anchor.set(0.5);
        valueText.name = "symbol_value";
        valueText.zIndex = 1;
        valueText.alpha = 0;
        valueText.roundPixels = true;
        symbolContainer.addChild(valueText);

        valueTextBlurred = new PIXI.BitmapText("", {
          fontName: "font_blurred",
          fontSize: fontSize,
        });
        valueTextBlurred.anchor.set(0.5);
        valueTextBlurred.name = "symbol_value_blurred";
        valueTextBlurred.zIndex = 1;
        valueTextBlurred.alpha = 0;
        valueTextBlurred.roundPixels = true;
        symbolContainer.addChild(valueTextBlurred);
      }

      this.spinningSprites.push({
        container: symbolContainer,
        normalSprite,
        blurredSprite,
        valueText,
        valueTextBlurred,
        currentSymbolId: null,
      });

      this.spinningContainer.addChild(symbolContainer);
    }

    this._updateSpinningSprites();
    this.hasInitializedTextures = true;
    const visibleStart = VISIBLE_SYMBOL_START_INDEX;
    const visibleEnd = visibleStart + VISIBLE_SYMBOL_COUNT;
    this.visibleSprites = this.spinningSprites.slice(visibleStart, visibleEnd);
  }

  // Calculates uniform symbol scale based on standard symbol reference height.
  // All symbols get the SAME scale factor — premium symbols with larger sourceSize
  // naturally overflow their cells, preserving the intended size relationships.
  // Cached per container dimensions for performance (scale is symbol-independent).
  _calculateSymbolScale(symbolName, containerWidth, containerHeight) {
    if (!containerHeight) return 1;

    // Check cache — uniform scale only depends on container dimensions
    const cacheKey = `uniform_${containerWidth}_${containerHeight}`;
    if (this._nativeDimensionsCache.has(cacheKey)) {
      return this._nativeDimensionsCache.get(cacheKey);
    }

    // Uniform scale: all symbols scale relative to the standard symbol height (168px).
    // Premium symbols (sourceSize > 168) will overflow proportionally.
    // Standard symbols (sourceSize = 168) will fit exactly.
    const scale = containerHeight / Settings.SYMBOL_HEIGHT;

    // Cache result
    this._nativeDimensionsCache.set(cacheKey, scale);

    return scale;
  }

  // Sets sprite textures (normal/blurred) and coin value text
  _setSpriteTexture(spriteData, symbolId) {
    if (spriteData.currentSymbolId === symbolId) {
      return;
    }

    const symbolInfo = symbolData[symbolId];
    if (!symbolInfo) {
      spriteData.normalSprite.texture = PIXI.Texture.EMPTY;
      spriteData.normalSprite.alpha = 0.001;
      if (spriteData.blurredSprite) {
        spriteData.blurredSprite.texture = PIXI.Texture.EMPTY;
        spriteData.blurredSprite.alpha = 0.001;
      }
      if (spriteData.valueText) {
        spriteData.valueText.text = "";
        spriteData.valueText.alpha = 0;
      }
      spriteData.currentSymbolId = null;
      return;
    }

    spriteData.currentSymbolId = symbolId;
    const symbolName = symbolInfo.name;
    const textureName = symbolInfo.texture;
    const symbolValue = symbolInfo.value;

    // If texture is null or empty, set empty textures and skip processing
    if (!textureName || textureName === null) {
      spriteData.normalSprite.texture = PIXI.Texture.EMPTY;
      spriteData.normalSprite.alpha = 0.001;
      if (spriteData.blurredSprite) {
        spriteData.blurredSprite.texture = PIXI.Texture.EMPTY;
        spriteData.blurredSprite.alpha = 0.001;
      }
      if (spriteData.valueText) {
        spriteData.valueText.text = "";
        spriteData.valueText.alpha = 0;
      }
      spriteData.currentSymbolId = null;
      return;
    }

    if (this.spritesheet && textureName) {
      const texture =
        this.spritesheet.textures?.[`${textureName}.png`] || PIXI.Texture.EMPTY;
      spriteData.normalSprite.texture = texture;
      spriteData.normalSprite.alpha = this.isBlurred ? 0.001 : 1;

      if (this.spritesheetBlurred && spriteData.blurredSprite) {
        const blurredTexture =
          this.spritesheetBlurred.textures?.[`${textureName}_blurred.png`] ||
          PIXI.Texture.EMPTY;
        spriteData.blurredSprite.texture = blurredTexture;
        spriteData.blurredSprite.alpha = this.isBlurred ? 1 : 0.001;
      }
    }

    if (symbolName) {
      const scale = this._calculateSymbolScale(
        symbolName,
        this.symbolWidth,
        this.symbolHeight
      );
      if (spriteData.normalSprite) {
        spriteData.normalSprite.scale.set(scale);
      }
      if (spriteData.blurredSprite) {
        spriteData.blurredSprite.scale.set(scale);
      }
    }

    // Update coin value text — skip entirely when symbol has no value (most symbols during spin)
    // This avoids store.getState(), updateText(), regex, and scale calculations in the hot loop
    if (spriteData.valueText && symbolValue) {
      const displayValue = this._getCoinDisplayValue(symbolValue);
      spriteData.valueText.text = displayValue;
      spriteData.valueText.alpha =
        displayValue !== "" ? (this.isBlurred ? 0 : 1) : 0;
      spriteData.valueText.y = 0;

      // Apply scaling logic if text has content
      if (displayValue !== "") {
        // Reset scale first
        spriteData.valueText.scale.set(1);

        // Force update to get accurate measurements
        spriteData.valueText.updateText();

        // Calculate available width (with padding)
        const coinWidth = this.symbolWidth;
        const paddingPercent = 0.12; // 12% padding on each side
        const maxTextWidth = coinWidth * (1 - paddingPercent * 2);

        const actualTextWidth = spriteData.valueText.width;

        // Only apply scaling if text is too wide
        if (actualTextWidth > maxTextWidth) {
          const digitCount = displayValue.replace(/[^0-9]/g, '').length;

          if (digitCount <= 4) {
            const scaleX = maxTextWidth / actualTextWidth;
            spriteData.valueText.scale.x = scaleX;
          } else if (digitCount >= 5 && digitCount <= 8) {
            const scaleX = maxTextWidth / actualTextWidth;
            spriteData.valueText.scale.x = scaleX;
          } else {
            const scale = maxTextWidth / actualTextWidth;
            spriteData.valueText.scale.set(scale);
          }
        }
      }

      // Update blurred text with same value and scaling
      if (spriteData.valueTextBlurred) {
        spriteData.valueTextBlurred.text = displayValue;
        spriteData.valueTextBlurred.alpha =
          displayValue !== "" ? (this.isBlurred ? 1 : 0) : 0;
        spriteData.valueTextBlurred.y = 0;

        if (displayValue !== "") {
          spriteData.valueTextBlurred.scale.set(1);
          spriteData.valueTextBlurred.updateText();

          const coinWidth = this.symbolWidth;
          const paddingPercent = 0.1;
          const maxTextWidth = coinWidth * (1 - paddingPercent * 2);

          const actualTextWidth = spriteData.valueTextBlurred.width;

          if (actualTextWidth > maxTextWidth) {
            const digitCount = displayValue.replace(/[^0-9]/g, '').length;

            if (digitCount <= 4) {
              const scaleX = maxTextWidth / actualTextWidth;
              spriteData.valueTextBlurred.scale.x = scaleX;
            } else if (digitCount >= 5 && digitCount <= 8) {
              const scaleX = maxTextWidth / actualTextWidth;
              spriteData.valueTextBlurred.scale.x = scaleX;
            } else {
              const scale = maxTextWidth / actualTextWidth;
              spriteData.valueTextBlurred.scale.set(scale);
            }
          }
        }
      }
    } else if (spriteData.valueText) {
      // Non-coin symbol: clear text without heavy processing
      spriteData.valueText.text = "";
      spriteData.valueText.alpha = 0;
      if (spriteData.valueTextBlurred) {
        spriteData.valueTextBlurred.text = "";
        spriteData.valueTextBlurred.alpha = 0;
      }
    }
  }

  // Generates random symbol - uses boosted coin probability in tension mode (only if Hold & Win enabled)
  _generateRandomSymbolId() {
    // Only generate coins if Hold & Win feature is enabled
    if (FEATURES_CONFIG.HOLD_AND_WIN.ENABLED && COIN_SYMBOL_IDS.length > 0) {
      const coinProbability = COIN_GENERATION_PROBABILITY;

      if (Math.random() < coinProbability) {
        return COIN_SYMBOL_IDS[
          Math.floor(Math.random() * COIN_SYMBOL_IDS.length)
        ];
      }
    }

    return this._pickRandomSymbolId();
  }

  // Initializes textures - visible sprites use API data, buffers use random symbols
  _updateSpinningSprites() {
    if (!this.spinningSprites.length) return;

    const shouldSkipSprite0 =
      this.pendingLandingSymbolId !== null && this._isLandingSpriteVisible();

    // Check if we have initial reels data from API
    const hasInitialReels =
      this.reels && this.reels.length >= VISIBLE_SYMBOL_COUNT;

    for (let i = 0; i < this.spinningSprites.length; i++) {
      if (i === 0 && shouldSkipSprite0) continue;

      let symbolId;

      if (
        hasInitialReels &&
        i >= VISIBLE_SYMBOL_START_INDEX &&
        i < VISIBLE_SYMBOL_START_INDEX + VISIBLE_SYMBOL_COUNT
      ) {
        const reelIndex = i - VISIBLE_SYMBOL_START_INDEX;
        symbolId = this.reels[reelIndex];
      } else {
        symbolId = this._generateRandomSymbolId();
      }

      this._setSpriteTexture(this.spinningSprites[i], symbolId);
    }
  }

  _resetSpritePositionsForSpin() {
    if (!this.spinningSprites.length) return;
    for (let i = 0; i < this.spinningSprites.length; i++) {
      this.spinningSprites[i].container.y = i * this.symbolHeight;
    }
  }

  // Checks if landing zone sprites are visible - determines when to apply API landing symbols
  _isLandingSpriteVisible() {
    if (!this.spinningContainer || this.spinningSprites.length < SPRITE_COUNT) {
      return false;
    }

    const containerY = this.spinningContainer.y;
    const visibleTop = -this.symbolHeight / 2;
    const visibleBottom = this.symbolHeight / 2;
    const landingZoneStart = 1;
    const landingZoneEnd = 1 + VISIBLE_SYMBOL_COUNT;

    // Check if all landing zone sprites (1-4) are within visible bounds
    for (let i = landingZoneStart; i < landingZoneEnd; i++) {
      const sprite = this.spinningSprites[i];
      if (!sprite) return false;

      const absoluteY = containerY + sprite.container.y;
      if (absoluteY < visibleTop || absoluteY > visibleBottom) {
        return false;
      }
    }
    return true;
  }

  // Applies API landing symbols to landing zone - sprites drop into visible area on stop
  _setLandingSymbolOnVisibleSprites(symbolId = null) {
    const landingZoneStart = 1;
    const landingZoneEnd = 1 + VISIBLE_SYMBOL_COUNT;

    if (this.reels && this.reels.length >= VISIBLE_SYMBOL_COUNT) {
      for (let i = landingZoneStart; i < landingZoneEnd; i++) {
        if (this.spinningSprites.length > i) {
          const apiIndex = i - 1;
          const symbolIdForThisPosition = this.reels[apiIndex];

          if (
            symbolIdForThisPosition !== undefined &&
            symbolData[symbolIdForThisPosition]
          ) {
            this._setSpriteTexture(
              this.spinningSprites[i],
              symbolIdForThisPosition
            );
          }
        }
      }
    } else if (symbolId !== null) {
      for (let i = landingZoneStart; i < landingZoneEnd; i++) {
        if (this.spinningSprites.length > i) {
          this._setSpriteTexture(this.spinningSprites[i], symbolId);
        }
      }
    }
  }

  // Start spin with wind-up animation
  spin() {
    if (this.isPlaying || this.isWindingUp) return;

    // Clear all previous animations and timeouts to prevent buildup
    this.clearAllTimeouts();

    // DON'T reset expanding wild flag here - let it persist until new API data arrives
    // This prevents blinking when spinning after expanding wild
    // Flag will be reset in updateReels() when new data arrives from API

    this.lockedSpinSpeed = this.autoPlaySpeed;

    if (this._pendingPositionUpdate) {
      this._applyPendingResize();
    }

    if (!this.spinningSprites.length) {
      this._createSpinningSprites();
      this.hasInitializedTextures = false;
    } else if (this.spinningSprites.length !== SPRITE_COUNT) {
      this._resetSpritePositionsForSpin();
    }

    if (this.spinningSprites.length > 0) {
      for (let i = 0; i < this.spinningSprites.length; i++) {
        this.spinningSprites[i].container.y = i * this.symbolHeight;
      }
    }

    this.stopTweenActive = false;
    this.stopAnimationProgress = 0;

    if (!this.hasInitializedTextures) {
      this._initializeSpinningTextures();
      this.hasInitializedTextures = true;
    }


    if (FEATURES_CONFIG.SPIN.WIND_UP_DURATION === 0) {
      this._startSpinAnimation();
      return;
    }

    this.isWindingUp = true;

    // Wind-up distance and duration match the bounce overshoot exactly.
    // easeOutBack overshoots by 4s³/(27(s+1)²) of total landing distance.
    // Bounce portion takes 2s/(3(s+1)) fraction of total stop time.
    const speedConfig = this._getSpeedConfig();
    const s = speedConfig.BOUNCE_OVERSHOOT;
    const overshootFraction = (4 * s * s * s) / (27 * (s + 1) * (s + 1));
    const windUpDistance = -(overshootFraction * STOP_MOVE_DISTANCE_SYMBOLS * 1.8) * this.symbolHeight;

    // Calculate bounce duration from the stop timing to match wind-up timing
    const spinVelocity = (this.symbolHeight / speedConfig.SPEED_MS_PER_SYMBOL_HEIGHT) * 1000;
    const totalStopDuration = ((s + 3) * STOP_MOVE_DISTANCE_SYMBOLS * this.symbolHeight * 1000) / spinVelocity * speedConfig.STOP_SLOWDOWN_FACTOR;
    const bounceFraction = (2 * s) / (3 * (s + 1));
    const windUpSpeedFactor = FEATURES_CONFIG.SPIN.WIND_UP_SPEED_FACTOR ?? 1.0;
    const windUpDuration = Math.round(totalStopDuration * bounceFraction * windUpSpeedFactor) || FEATURES_CONFIG.SPIN.WIND_UP_DURATION;

    const currentY = this.spinningContainer.y;
    const originalY = 0;

    if (Math.abs(currentY) > 0.001) {
      this.spinningContainer.y = originalY;
    }

    // Wind-up: pull up, then release back to origin.
    // Spin starts in the complete callback — zero overlap between anime.js and Ticker rAF loops.
    this.windUpAnimation = anime({
      targets: this.spinningContainer,
      y: [
        { value: originalY + windUpDistance, duration: windUpDuration, easing: 'easeOutCubic' },
        { value: originalY, duration: windUpDuration * 0.6, easing: 'easeInCubic' },
      ],
      complete: () => {
        this.spinningContainer.y = originalY;
        this.windUpAnimation = null;

        // Mark wind-up as done BEFORE dispatching — manual stop checks this flag
        this.isWindingUp = false;

        // Start spin animation FIRST — highest priority, no frame delay
        this._startSpinAnimation();

        // Defer Redux dispatch to AFTER paint — setTimeout(0) allows React 18 to batch
        // queueMicrotask runs before paint, causing 5 separate React re-renders per frame
        setTimeout(() => {
          this.dispatch(incrementWindupCount());
        }, 0);
      },
    });
  }

  // Start spin without wind-up (used for quick mode)
  spinNoWindUp() {
    if (this.isPlaying || this.isWindingUp) return;

    this.clearAllTimeouts();
    this.lockedSpinSpeed = this.autoPlaySpeed;

    if (this._pendingPositionUpdate) {
      this._applyPendingResize();
    }

    if (!this.spinningSprites.length) {
      this._createSpinningSprites();
      this.hasInitializedTextures = false;
    } else if (this.spinningSprites.length !== SPRITE_COUNT) {
      this._resetSpritePositionsForSpin();
    }

    if (this.spinningSprites.length > 0) {
      for (let i = 0; i < this.spinningSprites.length; i++) {
        this.spinningSprites[i].container.y = i * this.symbolHeight;
      }
    }

    this.stopTweenActive = false;
    this.stopAnimationProgress = 0;

    if (!this.hasInitializedTextures) {
      this._initializeSpinningTextures();
      this.hasInitializedTextures = true;
    }


    // Jump straight to spinning — FIRST, before any dispatch
    this._startSpinAnimation();

    // Defer Redux dispatch to AFTER paint
    setTimeout(() => {
      this.dispatch(incrementWindupCount());
    }, 0);
  }

  // Start turbo spin (no wind-up, no animation, instant setup)
  spinTurbo() {
    if (this.isPlaying || this.isWindingUp) return;

    // Clear all previous animations and timeouts to prevent buildup
    this.clearAllTimeouts();

    // DON'T reset expanding wild flag here - let it persist until new API data arrives
    // This prevents blinking when spinning after expanding wild
    // Flag will be reset in updateReels() when new data arrives from API

    this.lockedSpinSpeed = this.autoPlaySpeed;

    if (this._pendingPositionUpdate) {
      this._applyPendingResize();
    }

    if (!this.spinningSprites.length) {
      this._createSpinningSprites();
      this.hasInitializedTextures = false;
    } else if (this.spinningSprites.length !== SPRITE_COUNT) {
      this._resetSpritePositionsForSpin();
    }

    this.stopTweenActive = false;
    this.stopAnimationProgress = 0;

    this.hasStopped = false;
    this.isStopping = false;
    this.isBlurred = false;
    this.speedMultiplier = 1.0;
    this.isTensionMode = false;
    this.spinStartTime = performance.now();

    this._switchToNormalTextures();

    for (let i = 0; i < this.spinningSprites.length; i++) {
      this.spinningSprites[i].container.y = i * this.symbolHeight;
    }

    this.spinningContainer.y = 0;

    if (!this.hasInitializedTextures) {
      this._initializeSpinningTextures();
      this.hasInitializedTextures = true;
    }

    this.isPlaying = true;
  }

  // Stop turbo spin (instant drop with bounce)
  stopSpinTurbo() {
    if (this.isStopping || this.hasStopped || !this.isPlaying) return;

    this.isStopping = true;
    this.isPlaying = false;
    this.stopTweenActive = true;

    // Stop the spin ticker — prevent competing rAF loops during drop animation
    if (this.tickerCallback) {
      PIXI.Ticker.shared.remove(this.tickerCallback);
      this.tickerCallback = null;
    }

    // Unblur all sprites so landing symbols are sharp during drop
    this._switchToNormalTextures();

    const landingZoneStart = 1;
    const landingZoneEnd = 1 + VISIBLE_SYMBOL_COUNT;

    for (let i = landingZoneStart; i < landingZoneEnd; i++) {
      const apiIndex = i - 1;
      const symbolId = this.reels[apiIndex];
      if (symbolId !== undefined) {
        this._setSpriteTexture(this.spinningSprites[i], symbolId);
      }
    }

    const dropDistance = STOP_MOVE_DISTANCE_SYMBOLS * this.symbolHeight;
    const turboConfig = FEATURES_CONFIG.SPIN.AUTO_PLAY_SPEEDS.TURBO;
    const dropDuration = turboConfig.DROP_DURATION || 300;
    const bounceOvershoot = turboConfig.BOUNCE_OVERSHOOT ?? 1.5; // Use nullish coalescing to allow 0

    let lastProgress = 0;
    this.stopAnimationProgress = 0;
    this.stopAnimationTotalDistance = dropDistance;

    this.activeStopAnimation = anime({
      targets: { progress: 0 },
      progress: dropDistance,
      duration: dropDuration,
      easing: function () {
        return function (t, overshoot = bounceOvershoot) {
          return --t * t * ((overshoot + 1) * t + overshoot) + 1;
        };
      },
      update: (anim) => {
        const currentProgress = anim.animations[0].currentValue;
        const delta = currentProgress - lastProgress;
        lastProgress = currentProgress;

        this.stopAnimationProgress = currentProgress / dropDistance;

        if (Math.abs(delta) < 0.0001) return;

        for (let i = 0; i < this.spinningSprites.length; i++) {
          this.spinningSprites[i].container.y += delta;
        }

        // Use cached wrap threshold
        const wrapThreshold = this._wrapThresholdCache || SPRITE_COUNT * this.symbolHeight;
        for (let i = 0; i < this.spinningSprites.length; i++) {
          if (this.spinningSprites[i].container.y >= wrapThreshold) {
            this.spinningSprites[i].container.y -= wrapThreshold;
          }
        }
      },
      complete: () => {
        this.activeStopAnimation = null;
        this.stopAnimationProgress = 0;

        // Apply deferred resize if pending
        if (this._pendingPositionUpdate) {
          const wasStopped = this.hasStopped;
          const wasStopTweenActive = this.stopTweenActive;
          this.stopTweenActive = false;
          this.activeStopAnimation = null;

          this._applyPendingResize();

          this.hasStopped = wasStopped;
          this.stopTweenActive = wasStopTweenActive;
        }

        // Rotate array: move last N sprites to front (single O(n) operation)
        const cut = this.spinningSprites.length - STOP_MOVE_DISTANCE_SYMBOLS;
        const tail = this.spinningSprites.splice(cut);
        this.spinningSprites.unshift(...tail);

        for (let i = 0; i < this.spinningSprites.length; i++) {
          this.spinningSprites[i].container.y = i * this.symbolHeight;
        }

        // Set critical state flags immediately
        this.spinningContainer.y = 0;
        this.hasStopped = true;
        this.isStopping = false;
        this.stopTweenActive = false;
        this.lockedSpinSpeed = null;

        // 🚀 FIRE LANDING ANIMATIONS IMMEDIATELY - highest priority for smoothest UX
        if (this.onReelStop) {
          this.onReelStop(this.reelIndex, this.reels);
        }


        // Play sound when animation completes — reel_stop once + special symbol sound
        if (this.reelIndex === Settings.REEL_COUNT - 1) {
          soundManager.play(SOUND_NAME.reel_stop);
          const specialSound = this._getTurboModeSound();
          if (specialSound && specialSound !== SOUND_NAME.reel_stop) {
            soundManager.play(specialSound);
          }
        }

        if (this.reelIndex === Settings.REEL_COUNT - 1) {
          this.dispatch(setSpinComplete(true));
        }
      },
    });
  }

  // Starts spin animation after wind-up completes
  _startSpinAnimation() {
    this.isPlaying = true;
    this.hasStopped = false;
    this.isStopping = false;
    this.isBlurred = false;
    this.stopTweenActive = false;
    this.speedMultiplier = 1.0;
    this.isTensionMode = false;
    this.isDecelerating = false;
    this.decelerationStartTime = null;
    this.accelerationMultiplier = 0; // Start from 0, ramp up
    this.currentVelocity = 0;

    // Start reel_spin sound when first reel begins actual spinning (after wind-up)
    if (this.reelIndex === 0) {
      soundManager.play(SOUND_NAME.reel_spin);
    }

    const speedConfig = this._getSpeedConfig();
    this.normalizedSpinSpeed =
      (this.symbolHeight / speedConfig.SPEED_MS_PER_SYMBOL_HEIGHT) * 1000;
    this.spinStartTime = performance.now();
    this.lastCalculatedPosition = 0;

    // No timer-based blur — velocity-based blur handles it in updateSymbolsAbsolute()

    this.tickerCallback = () => {
      if (this.spinStartTime === null || this.hasStopped) {
        return;
      }
      this.updateSymbolsAbsolute();
    };

    PIXI.Ticker.shared.add(this.tickerCallback);
  }

  // Initiates stop sequence - cannot stop during wind-up
  stopSpin() {
    if (this.isWindingUp) {
      return;
    }

    if (this.isStopping || this.hasStopped || !this.isPlaying) return;

    this.isStopping = true;
    this.speedMultiplier = 1.0;
    this.isTensionMode = false;
  }

  // Tension mode — no-op for base game reels.
  // Tension only extends the delay before stop (handled by ReelControlContext).
  // The reel keeps spinning at normal speed with no visual changes.
  spinTension() {
    // No-op: reel animation is not altered during tension
  }

  // Stops tension mode spin — just triggers normal stop
  stopSpinTension() {
    if (this.isStopping || this.hasStopped || !this.isPlaying) return;
    this.isStopping = true;
  }

  // Freeze spin instantly - stops all animations without landing
  // Used for error popups where reels should freeze at current position
  freezeSpin() {
    // Stop ticker immediately
    if (this.tickerCallback) {
      PIXI.Ticker.shared.remove(this.tickerCallback);
      this.tickerCallback = null;
    }

    // Stop wind-up if active
    if (this.windUpAnimation) {
      this.windUpAnimation.pause();
      anime.remove(this.spinningContainer); // Force-eject from anime.js internal tracker
      this.windUpAnimation = null;
      this.isWindingUp = false;
    }

    // Clear ticker-based stop animation params
    this._stopStartTime = null;
    this._stopDistance = 0;
    this._stopDuration = 0;
    this._stopOvershoot = 0;
    this._stopLastEasedPos = 0;
    this.activeStopAnimation = null;


    // Set state flags
    this.isPlaying = false;
    this.hasStopped = true;
    this.isStopping = false;
    this.stopTweenActive = false;
    this.isTensionMode = false;
    this.speedMultiplier = 1.0;
    this.spinStartTime = null;
    this.lockedSpinSpeed = null;
    this.accelerationMultiplier = 1.0;
    this.currentVelocity = 0;
    this.isDecelerating = false;
    this.decelerationStartTime = null;

    // Clear all pending timeouts
    this.clearAllTimeouts();

    // DO NOT call onReelStop - no landing animations
    // DO NOT dispatch setSpinComplete - popup handles flow
    // DO NOT switch textures - keep current blur state
  }

  // Restore reel to idle state with specific symbols
  // Used after error recovery to show previous round's symbols properly
  // @param {Array} symbolIds - Array of symbol IDs to restore
  // @param {number|null} betValue - Optional bet value to use for coin texts (for error recovery)
  restoreToIdleState(symbolIds, betValue = null) {
    // Clear all animations and timeouts
    this.clearAllTimeouts();

    // Stop ticker if active
    if (this.tickerCallback) {
      PIXI.Ticker.shared.remove(this.tickerCallback);
      this.tickerCallback = null;
    }

    // Stop any active animations
    if (this.windUpAnimation) {
      this.windUpAnimation.pause();
      anime.remove(this.spinningContainer); // Force-eject from anime.js internal tracker
      this.windUpAnimation = null;
      this.isWindingUp = false;
    }

    // Clear ticker-based stop animation params
    this._stopStartTime = null;
    this._stopDistance = 0;
    this._stopDuration = 0;
    this._stopOvershoot = 0;
    this._stopLastEasedPos = 0;
    this.activeStopAnimation = null;


    // Reset state flags
    this.isPlaying = false;
    this.hasStopped = true;
    this.isStopping = false;
    this.stopTweenActive = false;
    this.isTensionMode = false;
    this.isBlurred = false;
    this.speedMultiplier = 1.0;
    this.spinStartTime = null;
    this.lockedSpinSpeed = null;
    this.accelerationMultiplier = 1.0;
    this.currentVelocity = 0;
    this.isDecelerating = false;
    this.decelerationStartTime = null;

    // Switch to normal (non-blurred) textures
    this._switchToNormalTextures();

    // Ensure sprites exist
    if (!this.spinningSprites.length) {
      this._createSpinningSprites();
    }

    // Reset container position to default
    this.spinningContainer.y = 0;

    // Position all sprites to their default positions
    for (let i = 0; i < this.spinningSprites.length; i++) {
      this.spinningSprites[i].container.y = i * this.symbolHeight;
    }

    // Set visible sprites to show the provided symbols
    // Visible sprites are at indices VISIBLE_SYMBOL_START_INDEX to VISIBLE_SYMBOL_START_INDEX + VISIBLE_SYMBOL_COUNT
    if (symbolIds && Array.isArray(symbolIds)) {
      // Store original bet and temporarily use historical bet if provided
      const originalBet = this.bet;
      if (betValue !== null && betValue !== undefined) {
        this.bet = betValue;
      }

      for (let i = 0; i < VISIBLE_SYMBOL_COUNT && i < symbolIds.length; i++) {
        const spriteIndex = VISIBLE_SYMBOL_START_INDEX + i;
        const symbolId = symbolIds[i];
        if (symbolId !== undefined && this.spinningSprites[spriteIndex]) {
          this._setSpriteTexture(this.spinningSprites[spriteIndex], symbolId);
        }
      }

      // Fill other sprites with random symbols for smooth next spin
      for (let i = 0; i < this.spinningSprites.length; i++) {
        if (i < VISIBLE_SYMBOL_START_INDEX || i >= VISIBLE_SYMBOL_START_INDEX + VISIBLE_SYMBOL_COUNT) {
          const randomSymbolId = this._generateRandomSymbolId();
          this._setSpriteTexture(this.spinningSprites[i], randomSymbolId);
        }
      }

      // Update reels data for next spin
      this.reels = symbolIds;
      this.hasPendingLandingSymbols = false;

      // Trigger idle animations (coin animations, wild animations, etc.)
      // Pass betValue to overlay controller for correct coin text display during recovery
      if (this.onReelStop) {
        this.onReelStop(this.reelIndex, symbolIds, betValue);
      }

      // Restore original bet after idle animations are triggered
      if (betValue !== null && betValue !== undefined) {
        this.bet = originalBet;
      }
    }
  }

  // Main animation loop
  updateSymbolsAbsolute() {
    if (this.spinStartTime === null || this.hasStopped) {
      return;
    }

    // --- Ticker-based stop animation (replaces anime.js for seamless handoff) ---
    if (this.stopTweenActive && this._stopStartTime !== null) {
      const now = performance.now();
      const elapsed = now - this._stopStartTime;
      const t = Math.min(elapsed / this._stopDuration, 1.0);

      // BackOut easing: f(t) = (t-1)² × ((s+1)(t-1) + s) + 1
      const s = this._stopOvershoot;
      const t1 = t - 1;
      const easedT = t1 * t1 * ((s + 1) * t1 + s) + 1;
      const easedPosition = this._stopDistance * easedT;
      const delta = easedPosition - this._stopLastEasedPos;
      this._stopLastEasedPos = easedPosition;

      this.stopAnimationProgress = easedPosition / this._stopDistance;

      // Update blur during stop (velocity decreases as easing decelerates)
      // BackOut derivative: f'(t) = 3(s+1)(t-1)² + 2s(t-1)
      const easingVelocity = 3 * (s + 1) * t1 * t1 + 2 * s * t1;
      this.currentVelocity = Math.abs(easingVelocity) * 0.3;
      this._updateBlurFromVelocity();

      if (Math.abs(delta) > 0.0001) {
        for (let i = 0; i < this.spinningSprites.length; i++) {
          this.spinningSprites[i].container.y += delta;
        }

        const wrapThreshold = this._wrapThresholdCache || SPRITE_COUNT * this.symbolHeight;
        for (let i = 0; i < this.spinningSprites.length; i++) {
          if (this.spinningSprites[i].container.y >= wrapThreshold) {
            this.spinningSprites[i].container.y -= wrapThreshold;
          }
        }
      }

      if (t >= 1.0) {
        this._finalizeLanding();
      }
      return;
    }

    const now = performance.now();
    const elapsedTime = (now - this.spinStartTime) / 1000;
    const currentExpectedPosition = elapsedTime * this.normalizedSpinSpeed;

    let movement = currentExpectedPosition - this.lastCalculatedPosition;
    this.lastCalculatedPosition = currentExpectedPosition;

    // --- Acceleration ramp-up (ease-out: fast start, smooth arrival at full speed) ---
    const speedConfig = this._getSpeedConfig();
    const accelDuration = speedConfig.ACCELERATION_DURATION || 0;
    if (accelDuration > 0 && this.accelerationMultiplier < 1.0) {
      const elapsedMs = now - this.spinStartTime;
      const t = Math.min(elapsedMs / accelDuration, 1.0);
      // Ease-out cubic: starts fast, smoothly reaches 1.0
      this.accelerationMultiplier = 1 - (1 - t) * (1 - t) * (1 - t);
    } else {
      this.accelerationMultiplier = 1.0;
    }

    // Apply acceleration multiplier
    movement *= this.accelerationMultiplier;

    // --- Blur: smooth per-frame velocity-based crossfade ---
    this.currentVelocity = this.accelerationMultiplier;
    this._updateBlurFromVelocity();

    // Cache wrap threshold calculation
    if (
      this._wrapThresholdCache === null ||
      this._lastSymbolHeightForWrapThreshold !== this.symbolHeight
    ) {
      this._wrapThresholdCache = SPRITE_COUNT * this.symbolHeight;
      this._lastSymbolHeightForWrapThreshold = this.symbolHeight;
    }
    const wrapThreshold = this._wrapThresholdCache;
    const shouldUpdateTexture = !this.isStopping && !this.hasStopped;

    if (
      this.pendingLandingSymbolId !== null &&
      !this._isLandingSpriteVisible()
    ) {
      this._setLandingSymbolOnVisibleSprites(this.pendingLandingSymbolId);
      this.pendingLandingSymbolId = null;
    }

    let shouldExecuteStop = false;

    // Phase 1: Apply movement to ALL sprites uniformly (no break-and-snap)
    for (let i = 0; i < this.spinningSprites.length; i++) {
      this.spinningSprites[i].container.y += movement;
    }

    // Phase 2: Check for wraps AFTER all sprites have moved
    // Check the last sprite (highest Y) — at normal speeds only one wraps per frame
    const lastIndex = this.spinningSprites.length - 1;
    if (this.spinningSprites[lastIndex].container.y >= wrapThreshold) {
      if (this._pendingPositionUpdate) {
        this._applyPendingResize();
      }

      this.moveLastSymbolOnTop();

      if (
        this.pendingLandingSymbolId !== null &&
        !this._isLandingSpriteVisible()
      ) {
        this._setLandingSymbolOnVisibleSprites(this.pendingLandingSymbolId);
        this.pendingLandingSymbolId = null;
      }

      if (shouldUpdateTexture) {
        // Update texture on the sprite that just wrapped to top (now index 0)
        this.updateSymbolTextureRandomly(this.spinningSprites[0]);
      }

      if (this.isStopping && !this.hasStopped && !this.stopTweenActive) {
        shouldExecuteStop = true;
      }
    }

    if (shouldExecuteStop) {
      this._calculateAndExecuteStop();
    }
  }

  // Wraps last sprite to top — single-sprite teleport (no grid-snap on other sprites)
  moveLastSymbolOnTop() {
    const movedSymbol = this.spinningSprites.pop();
    this.spinningSprites.unshift(movedSymbol);

    // Teleport only the wrapped sprite to the top position.
    // Other sprites keep their exact Y positions — no grid-snap stutter.
    // The total strip height is the distance from top to wrap threshold.
    const totalStripHeight = SPRITE_COUNT * this.symbolHeight;
    movedSymbol.container.y -= totalStripHeight;
  }

  // Calculates current drift from expected positions
  _calculateCurrentDrift() {
    if (!this.spinningSprites.length) return 0;

    const visibleStartIndex = VISIBLE_SYMBOL_START_INDEX;
    const visibleEndIndex = visibleStartIndex + VISIBLE_SYMBOL_COUNT;

    let totalDrift = 0;
    let count = 0;

    for (
      let i = visibleStartIndex;
      i < visibleEndIndex && i < this.spinningSprites.length;
      i++
    ) {
      const expectedY = i * this.symbolHeight;
      const actualY = this.spinningSprites[i].container.y;
      const drift = actualY - expectedY;
      totalDrift += drift;
      count++;
    }

    const averageDrift = count > 0 ? totalDrift / count : 0;
    return averageDrift;
  }

  // Updates sprite with random symbol
  updateSymbolTextureRandomly(spriteData) {
    const randomSymbolId = this._generateRandomSymbolId();
    this._setSpriteTexture(spriteData, randomSymbolId);
  }

  // Initializes textures for spinning sprites
  _initializeSpinningTextures() {
    this.spinningSprites.forEach((spriteData, index) => {
      const randomSymbolId = this._generateRandomSymbolId();
      this._setSpriteTexture(spriteData, randomSymbolId);
    });
    this.hasInitializedTextures = true;
  }

  // Determines stop sound based on landed visible symbols only (ignores buffers)
  _getReelStopSound() {
    let hasWild = false;
    let hasScatter = false;
    let hasCoin = false;

    // Read from actual visible sprite positions — not this.reels which may include buffer data
    for (let i = 0; i < VISIBLE_SYMBOL_COUNT; i++) {
      const spriteData = this.spinningSprites[VISIBLE_SYMBOL_START_INDEX + i];
      const symbolId = spriteData?.currentSymbolId;
      if (symbolId === undefined || symbolId === null) continue;

      const symInfo = symbolData[symbolId];
      if (!symInfo) continue;

      if (symInfo.symbolPay === "wild") {
        hasWild = true;
      } else if (symInfo.symbolPay === "scatter") {
        hasScatter = true;
      } else if (FEATURES_CONFIG.HOLD_AND_WIN.ENABLED && COIN_SYMBOL_IDS.includes(symbolId)) {
        hasCoin = true;
      }
    }

    if (hasScatter) return SOUND_NAME.scatter_1;
    if (hasWild) return SOUND_NAME.wild;
    if (hasCoin) return SOUND_NAME.coin;
    return SOUND_NAME.reel_stop;
  }

  // Determines turbo mode stop sound by checking all reels - used on last reel
  _getTurboModeSound() {
    const state = store.getState();
    if (
      !state ||
      !state.game ||
      !state.game.currentRound ||
      !state.game.currentRound.reels
    ) {
      return this._getReelStopSound();
    }

    const allReels = state.game.currentRound.reels;
    let hasWild = false;
    let hasScatter = false;
    let hasCoin = false;

    for (let reelIndex = 0; reelIndex < allReels.length; reelIndex++) {
      const reel = allReels[reelIndex];
      if (!reel) continue;

      for (let i = 0; i < VISIBLE_SYMBOL_COUNT; i++) {
        const symbolId = reel[i];
        if (symbolId === undefined) continue;

        const symInfo = symbolData[symbolId];
        if (!symInfo) continue;

        if (symInfo.symbolPay === "wild") {
          hasWild = true;
          break;
        } else if (symInfo.symbolPay === "scatter") {
          hasScatter = true;
        } else if (FEATURES_CONFIG.HOLD_AND_WIN.ENABLED && COIN_SYMBOL_IDS.includes(symbolId)) {
          hasCoin = true;
        }
      }

      if (hasWild) break;
    }

    if (hasScatter) return SOUND_NAME.scatter_1;
    if (hasWild) return SOUND_NAME.wild;
    if (hasCoin) return SOUND_NAME.coin;
    return SOUND_NAME.reel_stop;
  }

  // Sets win state for this spin - affects sound playback
  setHasWinThisSpin(hasWin) {
    this.hasWinThisSpin = !!hasWin;
  }

  // Sets Hold & Win trigger state for this spin - affects spin complete delay
  setIsHoldAndWinTrigger(isTrigger) {
    this.isHoldAndWinTrigger = !!isTrigger;
  }

  // Gets speed configuration based on locked or current autoPlaySpeed
  // Cached for performance - invalidated when speed changes
  _getSpeedConfig() {
    const effectiveSpeed = this.lockedSpinSpeed || this.autoPlaySpeed;

    // Check cache validity
    if (
      this._speedConfigCache !== null &&
      this._lastLockedSpeed === this.lockedSpinSpeed &&
      this._lastAutoPlaySpeed === this.autoPlaySpeed
    ) {
      return this._speedConfigCache;
    }

    const config =
      FEATURES_CONFIG.SPIN.AUTO_PLAY_SPEEDS[effectiveSpeed?.toUpperCase()] ||
      FEATURES_CONFIG.SPIN.AUTO_PLAY_SPEEDS.AUTO;

    // Cache result
    this._speedConfigCache = config;
    this._lastLockedSpeed = this.lockedSpinSpeed;
    this._lastAutoPlaySpeed = this.autoPlaySpeed;

    return config;
  }


  // Unblur all sprites - instant transition
  _switchToNormalTextures() {
    if (!this.spritesheetBlurred) {
      return Promise.resolve();
    }


    this.spinningSprites.forEach((sprite) => {
      if (!sprite || !sprite.blurredSprite) return;
      sprite.normalSprite.alpha = 1;
      sprite.blurredSprite.alpha = 0;

      // Switch text back to normal
      if (sprite.valueText && sprite.valueText.text !== "") {
        sprite.valueText.alpha = 1;
      }
      if (sprite.valueTextBlurred) {
        sprite.valueTextBlurred.alpha = 0;
      }
    });

    this.isBlurred = false;

    return Promise.resolve();
  }

  // Selectively unblurs landing symbols during stop animation
  _switchToNormalTexturesSelective() {
    if (!this.spritesheetBlurred) return;


    const landingZoneStart = 1;
    const landingZoneEnd = 1 + VISIBLE_SYMBOL_COUNT;
    const bufferAbove = landingZoneStart - 1;
    const bufferBelow = landingZoneEnd;

    for (let i = 0; i < this.spinningSprites.length; i++) {
      const spriteData = this.spinningSprites[i];
      if (!spriteData || !spriteData.blurredSprite) continue;

      const isLandingZone = i >= landingZoneStart && i < landingZoneEnd;
      const isBufferAbove = i === bufferAbove;
      const isBufferBelow = i === bufferBelow;

      if (isLandingZone || isBufferAbove || isBufferBelow) {
        spriteData.normalSprite.alpha = 1;
        spriteData.blurredSprite.alpha = 0;

        // Switch text to normal for landing symbols
        if (spriteData.valueText && spriteData.valueText.text !== "") {
          spriteData.valueText.alpha = 1;
        }
        if (spriteData.valueTextBlurred) {
          spriteData.valueTextBlurred.alpha = 0;
        }
      }
    }

    this.isBlurred = false;
  }

  // Proportional blur: sets alpha of normal/blurred sprites based on velocity
  // Below threshold = fully sharp, above = gradually blurred proportional to speed
  _updateBlurFromVelocity() {
    if (!this.spritesheetBlurred) return;

    const speedConfig = this._getSpeedConfig();
    const threshold = speedConfig.BLUR_VELOCITY_THRESHOLD || 0.6;
    const velocity = this.currentVelocity;

    // Calculate blur amount: 0 = fully sharp, 1 = fully blurred
    // Ramp from threshold to 1.0 (full speed)
    let blurAmount;
    if (velocity < threshold) {
      blurAmount = 0;
    } else {
      // Normalized 0-1 within the blur range, squared for natural feel
      const t = (velocity - threshold) / (1.0 - threshold);
      blurAmount = Math.min(t * t, 1.0);
    }

    // Update isBlurred state for cleanup/state tracking
    this.isBlurred = blurAmount > 0.01;


    // Apply alpha proportionally to all sprites
    const normalAlpha = 1 - blurAmount;
    const blurredAlpha = blurAmount;

    for (let i = 0; i < this.spinningSprites.length; i++) {
      const spriteData = this.spinningSprites[i];
      if (!spriteData || !spriteData.blurredSprite) continue;

      // During stop animation, skip landing zone + buffer sprites (already unblurred)
      if (this.stopTweenActive) {
        const landingZoneEnd = 1 + VISIBLE_SYMBOL_COUNT;
        if (i >= 0 && i <= landingZoneEnd) continue;
      }

      spriteData.normalSprite.alpha = Math.max(normalAlpha, 0.001);
      spriteData.blurredSprite.alpha = blurredAlpha;

      // Blend coin value text alpha too
      if (spriteData.valueText && spriteData.valueText.text !== "") {
        spriteData.valueText.alpha = normalAlpha;
      }
      if (spriteData.valueTextBlurred && spriteData.valueTextBlurred.text !== "") {
        spriteData.valueTextBlurred.alpha = blurredAlpha;
      }
    }
  }
  // Ticker-based landing animation — runs in the same PIXI ticker as spin
  // for seamless handoff (no frame gap between spin and stop)
  // Uses BackOut easing with configurable overshoot for bounce feel
  _calculateAndExecuteStop() {
    if (this.hasStopped || this.stopTweenActive) return;
    this.stopTweenActive = true;

    // DON'T remove the ticker — the stop animation runs inside it

    const hasApiData = this.reels && this.reels.length >= VISIBLE_SYMBOL_COUNT;

    if (hasApiData) {
      if (
        this.spinningSprites.length >= SPRITE_COUNT &&
        !this._isLandingSpriteVisible()
      ) {
        this._setLandingSymbolOnVisibleSprites();
      }
    } else if (this.pendingLandingSymbolId !== null) {
      if (
        this.spinningSprites.length >= SPRITE_COUNT &&
        !this._isLandingSpriteVisible()
      ) {
        this._setLandingSymbolOnVisibleSprites(this.pendingLandingSymbolId);
        this.symbolId = this.pendingLandingSymbolId;
        this.pendingLandingSymbolId = null;
      }
    }

    const currentDrift = this._calculateCurrentDrift();
    const baseLandingDistance = STOP_MOVE_DISTANCE_SYMBOLS * this.symbolHeight;
    const adjustedLandingDistance = baseLandingDistance - currentDrift;

    const speedConfig = this._getSpeedConfig();
    const overshoot = speedConfig.BOUNCE_OVERSHOOT;

    // Landing duration: fixed from config, or velocity-matched fallback
    let stopDuration;
    const landingDuration = speedConfig.LANDING_DURATION || null;
    if (landingDuration) {
      stopDuration = landingDuration;
    } else {
      const currentSpinVelocity = this.normalizedSpinSpeed;
      const velocityMatchedDuration = ((overshoot + 3) * adjustedLandingDistance * 1000) / currentSpinVelocity;
      stopDuration = velocityMatchedDuration * speedConfig.STOP_SLOWDOWN_FACTOR;
    }

    this._setLandingSymbolOnVisibleSprites();

    // Landing symbols are above the visible area — set them to normal (non-blurred)
    // textures immediately so they're already sharp when they scroll into view
    {
      const landingZoneEnd = 1 + VISIBLE_SYMBOL_COUNT;
      for (let i = 0; i < landingZoneEnd; i++) {
        const spriteData = this.spinningSprites[i];
        if (!spriteData) continue;
        spriteData.normalSprite.alpha = 1;
        if (spriteData.blurredSprite) spriteData.blurredSprite.alpha = 0;
        if (spriteData.valueText && spriteData.valueText.text !== "") {
          spriteData.valueText.alpha = 1;
        }
        if (spriteData.valueTextBlurred) {
          spriteData.valueTextBlurred.alpha = 0;
        }
      }

      this.isBlurred = false;
    }

    if (this.reels && this.reels.length > 1) {
      this.symbolId = this.reels[1];
    }

    // Store stop params for the ticker to use (replaces anime.js tween)
    this._stopStartTime = performance.now();
    this._stopDistance = adjustedLandingDistance;
    this._stopDuration = stopDuration;
    this._stopOvershoot = overshoot;
    this._stopLastEasedPos = 0;

    this.stopAnimationProgress = 0;
    this.stopAnimationTotalDistance = adjustedLandingDistance;
    this.isPlaying = false;
    this.isDecelerating = false;
    this.decelerationStartTime = null;
  }

  // Shared landing finalization — called when ticker-based stop animation completes
  _finalizeLanding() {
    // Remove ticker — stop animation is done
    if (this.tickerCallback) {
      PIXI.Ticker.shared.remove(this.tickerCallback);
      this.tickerCallback = null;
    }

    // Clear stop animation params
    this._stopStartTime = null;
    this._stopDistance = 0;
    this._stopDuration = 0;
    this._stopOvershoot = 0;
    this._stopLastEasedPos = 0;
    this.activeStopAnimation = null;
    this.stopAnimationProgress = 0;
    this.isDecelerating = false;
    this.decelerationStartTime = null;

    const hadPendingResize = this._pendingPositionUpdate;
    if (this._pendingPositionUpdate) {
      const wasStopped = this.hasStopped;
      const wasStopTweenActive = this.stopTweenActive;
      this.stopTweenActive = false;
      this.activeStopAnimation = null;

      this._applyPendingResize();

      this.hasStopped = wasStopped;
      this.stopTweenActive = wasStopTweenActive;
    }

    // Rotate array: move last N sprites to front (single O(n) operation)
    const cut = this.spinningSprites.length - STOP_MOVE_DISTANCE_SYMBOLS;
    const tail = this.spinningSprites.splice(cut);
    this.spinningSprites.unshift(...tail);

    for (let i = 0; i < this.spinningSprites.length; i++) {
      this.spinningSprites[i].container.y = i * this.symbolHeight;
    }

    // Set critical state flags immediately
    this.hasStopped = true;
    this.stopTweenActive = false;
    this.spinningContainer.y = 0;
    this.lockedSpinSpeed = null;
    this.currentVelocity = 0;

    // 🚀 FIRE LANDING ANIMATIONS IMMEDIATELY - highest priority for smoothest UX
    if (this.onReelStop) {
      this.onReelStop(this.reelIndex, this.reels);
    }

    // Fire onReelLanded on the NEXT frame — gives landing animations an uncontested
    // render frame before tension operations (dim, sound, spine) kick in
    if (this.onReelLanded) {
      const callback = this.onReelLanded;
      const reelIndex = this.reelIndex;
      requestAnimationFrame(() => callback(reelIndex));
    }

    // Heavy operations AFTER landing animations start (non-blocking for animations)
    if (hadPendingResize) {
      this._forceRescaleAllTextures();
    }

    // Unblur all remaining sprites (bottom buffer etc.)
    this._switchToNormalTextures();

    // Always play reel_stop, plus any special symbol sound on top
    soundManager.play(SOUND_NAME.reel_stop);
    const specialSound = this._getReelStopSound();
    if (specialSound && specialSound !== SOUND_NAME.reel_stop) {
      soundManager.play(specialSound);
    }

    if (this.reelIndex === Settings.REEL_COUNT - 1) {
      soundManager.stop(SOUND_NAME.reel_spin);
      this.dispatch(setSpinComplete(true));
    }
  }

  // Forces re-scaling of all sprite textures
  _forceRescaleAllTextures() {
    if (!this.spinningSprites.length) return;

    this.spinningSprites.forEach((spriteData) => {
      if (spriteData.currentSymbolId !== null) {
        const info = symbolData[spriteData.currentSymbolId];
        if (info?.name) {
          const scale = this._calculateSymbolScale(
            info.name,
            this.symbolWidth,
            this.symbolHeight
          );
          if (spriteData.normalSprite) {
            spriteData.normalSprite.scale.set(scale);
          }
          if (spriteData.blurredSprite) {
            spriteData.blurredSprite.scale.set(scale);
          }
        }
      }
    });
  }

  // Set landing symbol - typically called with API data during spin
  setLandingSymbol(symbolId) {
    if (symbolId === undefined || symbolId === null) {
      return;
    }

    if (this.isPlaying && this.spinningSprites.length > 0) {
      if (this._isLandingSpriteVisible()) {
        this.pendingLandingSymbolId = symbolId;
        this.symbolId = symbolId;
      } else {
        this.symbolId = symbolId;
        this.pendingLandingSymbolId = null;
        this._setLandingSymbolOnVisibleSprites(symbolId);
      }
      return;
    }

    this.symbolId = symbolId;
    this.updateSymbolTexture(symbolId);
  }

  // Applies pending resize
  _applyPendingResize() {
    if (!this._pendingPositionUpdate) return;

    const oldSymbolHeight = this._originalSymbolHeight || this.symbolHeight;
    const newSymbolHeight = this._targetSymbolHeight;
    const scaleRatio = newSymbolHeight / oldSymbolHeight;
    const isSpinning = this.spinStartTime && !this.hasStopped;
    const isStopAnimating = this.stopTweenActive && this.activeStopAnimation;

    if (isStopAnimating) {
      return;
    }

    this.symbolHeight = this._targetSymbolHeight;
    this.symbolWidth = this._targetSymbolWidth;
    this.nativeDimensions = null;

    // Invalidate caches when symbol height changes
    this._wrapThresholdCache = null;
    this._lastSymbolHeightForWrapThreshold = null;
    this._nativeDimensionsCache.clear(); // Clear symbol scale cache

    if (isSpinning && Math.abs(scaleRatio - 1.0) > 0.0001) {
      this.spinningSprites.forEach((spriteData) => {
        spriteData.container.y =
          Math.round(spriteData.container.y * scaleRatio * PRECISION_FACTOR) /
          PRECISION_FACTOR;
      });

      this.lastCalculatedPosition =
        Math.round(
          this.lastCalculatedPosition * scaleRatio * PRECISION_FACTOR
        ) / PRECISION_FACTOR;

      const speedConfig = this._getSpeedConfig();
      this.normalizedSpinSpeed =
        (this.symbolHeight / speedConfig.SPEED_MS_PER_SYMBOL_HEIGHT) * 1000;
    } else {
      this.spinningSprites.forEach((spriteData, i) => {
        spriteData.container.y = i * this.symbolHeight;
      });
    }

    if (this.spinningSprites.length) {
      this.spinningSprites.forEach((spriteData) => {
        if (spriteData.currentSymbolId !== null) {
          const info = symbolData[spriteData.currentSymbolId];
          if (info?.name) {
            const scale = this._calculateSymbolScale(
              info.name,
              this.symbolWidth,
              this.symbolHeight
            );
            if (spriteData.normalSprite) {
              spriteData.normalSprite.scale.set(scale);
            }
            if (spriteData.blurredSprite) {
              spriteData.blurredSprite.scale.set(scale);
            }
          }
        }
        // Update font size for coin value text (both normal and blurred)
        if (spriteData.valueText) {
          const displayValue = spriteData.valueText.text;
          const fontSize = this.symbolHeight * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER;
          spriteData.valueText.fontSize = fontSize;

          // Reset scale first
          spriteData.valueText.scale.set(1);

          // Force update to get accurate measurements with new font size
          spriteData.valueText.updateText();
          spriteData.valueText.dirty = true;

          // Recalculate scaling with new dimensions (if text has content)
          if (displayValue !== "") {
            const coinWidth = this.symbolWidth;
            const paddingPercent = 0.12;
            const maxTextWidth = coinWidth * (1 - paddingPercent * 2);
            const actualTextWidth = spriteData.valueText.width;

            if (actualTextWidth > maxTextWidth) {
              const digitCount = displayValue.replace(/[^0-9]/g, '').length;

              if (digitCount <= 4) {
                const scaleX = maxTextWidth / actualTextWidth;
                spriteData.valueText.scale.x = scaleX;
              } else if (digitCount >= 5 && digitCount <= 8) {
                const scaleX = maxTextWidth / actualTextWidth;
                spriteData.valueText.scale.x = scaleX;
              } else {
                const scale = maxTextWidth / actualTextWidth;
                spriteData.valueText.scale.set(scale);
              }
            }
          }

          // Update blurred text with same logic
          if (spriteData.valueTextBlurred) {
            spriteData.valueTextBlurred.fontSize = fontSize;

            // Reset scale first
            spriteData.valueTextBlurred.scale.set(1);

            // Force update to get accurate measurements with new font size
            spriteData.valueTextBlurred.updateText();
            spriteData.valueTextBlurred.dirty = true;

            // Recalculate scaling with new dimensions (if text has content)
            if (displayValue !== "") {
              const coinWidth = this.symbolWidth;
              const paddingPercent = 0.12;
              const maxTextWidth = coinWidth * (1 - paddingPercent * 2);
              const actualTextWidth = spriteData.valueTextBlurred.width;

              if (actualTextWidth > maxTextWidth) {
                const digitCount = displayValue.replace(/[^0-9]/g, '').length;

                if (digitCount <= 4) {
                  const scaleX = maxTextWidth / actualTextWidth;
                  spriteData.valueTextBlurred.scale.x = scaleX;
                } else if (digitCount >= 5 && digitCount <= 8) {
                  const scaleX = maxTextWidth / actualTextWidth;
                  spriteData.valueTextBlurred.scale.x = scaleX;
                } else {
                  const scale = maxTextWidth / actualTextWidth;
                  spriteData.valueTextBlurred.scale.set(scale);
                }
              }
            }
          }
        }
      });
    }

    this._pendingPositionUpdate = false;
    this._originalSymbolHeight = this.symbolHeight;
  }

  // Update dimensions when symbol size changes
  resizeSymbol(newSymbolWidth, newSymbolHeight) {
    this._targetSymbolHeight = newSymbolHeight;
    this._targetSymbolWidth = newSymbolWidth;

    if (
      this.symbolHeight === newSymbolHeight &&
      this.symbolWidth === this._targetSymbolWidth
    ) {
      this._pendingPositionUpdate = false;
      return;
    }

    const oldSymbolHeight = this.symbolHeight;

    // If spinning OR stopping, defer update to avoid mid-animation position chaos
    if ((this.isPlaying || this.stopTweenActive) && !this.hasStopped) {
      this._originalSymbolHeight = oldSymbolHeight;
      this._pendingPositionUpdate = true;
      return;
    }

    // Not animating: update immediately
    this.symbolHeight = newSymbolHeight;
    this.symbolWidth = this._targetSymbolWidth;
    this._originalSymbolHeight = oldSymbolHeight;
    this._pendingPositionUpdate = false;
    this.nativeDimensions = null;

    // Invalidate caches when symbol height changes
    this._wrapThresholdCache = null;
    this._lastSymbolHeightForWrapThreshold = null;
    this._nativeDimensionsCache.clear(); // Clear symbol scale cache

    if (this.spinningSprites.length) {
      this.spinningSprites.forEach((spriteData, i) => {
        spriteData.container.y = i * this.symbolHeight;
      });
    }

    if (this.spinningSprites.length) {
      this.spinningSprites.forEach((spriteData) => {
        if (spriteData.currentSymbolId !== null) {
          const info = symbolData[spriteData.currentSymbolId];
          if (info?.name) {
            const scale = this._calculateSymbolScale(
              info.name,
              this.symbolWidth,
              this.symbolHeight
            );
            if (spriteData.normalSprite) {
              spriteData.normalSprite.scale.set(scale);
            }
            if (spriteData.blurredSprite) {
              spriteData.blurredSprite.scale.set(scale);
            }
          }
        }
        // Update font size for coin value text (both normal and blurred)
        if (spriteData.valueText) {
          const displayValue = spriteData.valueText.text;
          const fontSize = this.symbolHeight * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER;
          spriteData.valueText.fontSize = fontSize;

          // Reset scale first
          spriteData.valueText.scale.set(1);

          // Force update to get accurate measurements with new font size
          spriteData.valueText.updateText();
          spriteData.valueText.dirty = true;

          // Recalculate scaling with new dimensions (if text has content)
          if (displayValue !== "") {
            const coinWidth = this.symbolWidth;
            const paddingPercent = 0.12;
            const maxTextWidth = coinWidth * (1 - paddingPercent * 2);
            const actualTextWidth = spriteData.valueText.width;

            if (actualTextWidth > maxTextWidth) {
              const digitCount = displayValue.replace(/[^0-9]/g, '').length;

              if (digitCount <= 4) {
                const scaleX = maxTextWidth / actualTextWidth;
                spriteData.valueText.scale.x = scaleX;
              } else if (digitCount >= 5 && digitCount <= 8) {
                const scaleX = maxTextWidth / actualTextWidth;
                spriteData.valueText.scale.x = scaleX;
              } else {
                const scale = maxTextWidth / actualTextWidth;
                spriteData.valueText.scale.set(scale);
              }
            }
          }

          // Update blurred text with same logic
          if (spriteData.valueTextBlurred) {
            spriteData.valueTextBlurred.fontSize = fontSize;

            // Reset scale first
            spriteData.valueTextBlurred.scale.set(1);

            // Force update to get accurate measurements with new font size
            spriteData.valueTextBlurred.updateText();
            spriteData.valueTextBlurred.dirty = true;

            // Recalculate scaling with new dimensions (if text has content)
            if (displayValue !== "") {
              const coinWidth = this.symbolWidth;
              const paddingPercent = 0.12;
              const maxTextWidth = coinWidth * (1 - paddingPercent * 2);
              const actualTextWidth = spriteData.valueTextBlurred.width;

              if (actualTextWidth > maxTextWidth) {
                const digitCount = displayValue.replace(/[^0-9]/g, '').length;

                if (digitCount <= 4) {
                  const scaleX = maxTextWidth / actualTextWidth;
                  spriteData.valueTextBlurred.scale.x = scaleX;
                } else if (digitCount >= 5 && digitCount <= 8) {
                  const scaleX = maxTextWidth / actualTextWidth;
                  spriteData.valueTextBlurred.scale.x = scaleX;
                } else {
                  const scale = maxTextWidth / actualTextWidth;
                  spriteData.valueTextBlurred.scale.set(scale);
                }
              }
            }
          }
        }
      });
    }
  }

  // Texture Visibility Control (for overlay spine animations)

  // Hides symbol texture to show overlay spine animation
  hideSymbolTexture(symbolIndex) {
    const spriteIndex = VISIBLE_SYMBOL_START_INDEX + symbolIndex;
    if (spriteIndex >= this.spinningSprites.length) return;

    const sprite = this.spinningSprites[spriteIndex];
    if (!sprite) return;

    if (sprite.normalSprite) {
      sprite.normalSprite.alpha = 0.001;
    }
    if (sprite.blurredSprite) {
      sprite.blurredSprite.alpha = 0.001;
    }
  }

  // Shows symbol texture (hides overlay animation)
  showSymbolTexture(symbolIndex) {
    const spriteIndex = VISIBLE_SYMBOL_START_INDEX + symbolIndex;
    if (spriteIndex >= this.spinningSprites.length) return;

    const sprite = this.spinningSprites[spriteIndex];
    if (!sprite) return;

    // Recalculate scale when showing sprite (fixes scaling after expanding wild animations)
    if (sprite.currentSymbolId !== null) {
      const info = symbolData[sprite.currentSymbolId];
      if (info?.name) {
        const scale = this._calculateSymbolScale(
          info.name,
          this.symbolWidth,
          this.symbolHeight
        );
        if (sprite.normalSprite) {
          sprite.normalSprite.scale.set(scale);
        }
        if (sprite.blurredSprite) {
          sprite.blurredSprite.scale.set(scale);
        }
      }
    }

    if (sprite.normalSprite) {
      sprite.normalSprite.alpha = 1;
    }
    if (sprite.blurredSprite) {
      sprite.blurredSprite.alpha = 0.001;
    }
  }

  // Shows all visible symbol textures (clears overlay animations)
  showAllSymbolTextures() {
    const start = VISIBLE_SYMBOL_START_INDEX;
    const end = start + VISIBLE_SYMBOL_COUNT;

    for (let i = start; i < end && i < this.spinningSprites.length; i++) {
      const sprite = this.spinningSprites[i];
      if (sprite) {
        // Recalculate scale when showing sprite (fixes scaling after expanding wild animations)
        if (sprite.currentSymbolId !== null) {
          const info = symbolData[sprite.currentSymbolId];
          if (info?.name) {
            const scale = this._calculateSymbolScale(
              info.name,
              this.symbolWidth,
              this.symbolHeight
            );
            if (sprite.normalSprite) {
              sprite.normalSprite.scale.set(scale);
            }
            if (sprite.blurredSprite) {
              sprite.blurredSprite.scale.set(scale);
            }
          }
        }

        if (sprite.normalSprite) {
          sprite.normalSprite.alpha = 1;
        }
        if (sprite.blurredSprite) {
          sprite.blurredSprite.alpha = 0.001;
        }
      }
    }
  }

  // Exposes sprites in format compatible with useWinController - returns only visible sprites
  get symbolsContainer() {
    const visibleStart = VISIBLE_SYMBOL_START_INDEX;
    const visibleEnd = visibleStart + VISIBLE_SYMBOL_COUNT;
    const visibleSprites = this.spinningSprites.slice(visibleStart, visibleEnd);

    return {
      children: visibleSprites.map((sprite, overlayIndex) => ({
        currentSymbolId: sprite.currentSymbolId,
        symbolId: sprite.currentSymbolId,
        overlayIndex: overlayIndex, // Direct mapping: 0, 1, 2, 3
      })),
    };
  }

  // Expanding Wild Support

  // Replaces all visible symbols with target symbol (e.g., wild expansion)
  // @param {number} targetSymbolId - Symbol ID to replace with
  // @param {boolean} hideTextures - If true, hides textures (for cascade mode). If false, keeps visible (for full-reel mode)
  replaceVisibleSymbols(targetSymbolId, hideTextures = true) {
    const symbolInfo = symbolData[targetSymbolId];
    if (!symbolInfo?.texture) return;

    const textureName = symbolInfo.texture;
    const texture = this.spritesheet?.textures?.[`${textureName}.png`];
    if (!texture) return;

    // Replace all visible sprites
    const start = VISIBLE_SYMBOL_START_INDEX;
    const end = start + VISIBLE_SYMBOL_COUNT;

    for (let i = start; i < end && i < this.spinningSprites.length; i++) {
      const sprite = this.spinningSprites[i];
      if (!sprite) continue;

      sprite.normalSprite.texture = texture;
      sprite.currentSymbolId = targetSymbolId;

      // Only hide textures if requested (cascade mode needs this, full-reel mode doesn't)
      if (hideTextures) {
        sprite.normalSprite.alpha = 0.001;
        sprite.blurredSprite.alpha = 0.001;
      }
    }
  }

  // Replaces single symbol at position (for cascading wild animation)
  replaceSymbolAtPosition(symbolIndex, targetSymbolId) {
    const symbolInfo = symbolData[targetSymbolId];
    if (!symbolInfo?.texture) return;

    // CRITICAL: Update the reels array for this position
    // This prevents blinking when next spin starts
    // Create a new array since the original might be read-only (from Redux state)
    if (this.reels && this.reels.length > symbolIndex) {
      this.reels = this.reels.map((id, idx) =>
        idx === symbolIndex ? targetSymbolId : id
      );
    }

    const spriteIndex = VISIBLE_SYMBOL_START_INDEX + symbolIndex;
    if (spriteIndex >= this.spinningSprites.length) return;

    const sprite = this.spinningSprites[spriteIndex];
    if (!sprite) return;


    // Force update by temporarily clearing currentSymbolId to bypass early exit in _setSpriteTexture
    sprite.currentSymbolId = null;

    // Use _setSpriteTexture to properly update ALL textures (normal, blurred, coin values)
    // This ensures when showSymbolTexture() is called later, it reveals the correct wild texture
    this._setSpriteTexture(sprite, targetSymbolId);

    // Then hide the textures for the animation overlay to show
    sprite.normalSprite.alpha = 0.001;
    sprite.blurredSprite.alpha = 0.001;
  }

  // Cleanup

  clearAllTimeouts() {
    // Clear all pending setTimeout calls
    if (this.pendingTimeouts && this.pendingTimeouts.length > 0) {
      this.pendingTimeouts.forEach(timeoutId => clearTimeout(timeoutId));
      this.pendingTimeouts = [];
    }

    // Stop and clear all anime.js animations
    if (this.windUpAnimation) {
      this.windUpAnimation.pause();
      anime.remove(this.spinningContainer); // Force-eject from anime.js internal tracker
      this.windUpAnimation = null;
    }

    if (this.activeStopAnimation) {
      this.activeStopAnimation.pause();
      anime.remove(this.activeStopAnimation); // Force-eject from anime.js internal tracker
      this.activeStopAnimation = null;
    }



    // Remove PIXI.Ticker callback
    if (this.tickerCallback) {
      PIXI.Ticker.shared.remove(this.tickerCallback);
      this.tickerCallback = null;
    }
  }

  _cleanupSpinningSprites() {
    if (this.spinningSprites.length) {
      this.spinningSprites.forEach((symbolData) => {
        try {
          if (symbolData.container?.parent && this.spinningContainer) {
            this.spinningContainer.removeChild(symbolData.container);
          }
          if (symbolData.normalSprite && !symbolData.normalSprite.destroyed) {
            symbolData.normalSprite.destroy();
          }
          if (symbolData.blurredSprite && !symbolData.blurredSprite.destroyed) {
            symbolData.blurredSprite.destroy();
          }
          if (symbolData.valueText && !symbolData.valueText.destroyed) {
            symbolData.valueText.destroy();
          }
          if (
            symbolData.valueTextBlurred &&
            !symbolData.valueTextBlurred.destroyed
          ) {
            symbolData.valueTextBlurred.destroy();
          }
          if (symbolData.container && !symbolData.container.destroyed) {
            symbolData.container.destroy();
          }
        } catch (e) {
          // ignore
        }
      });
      this.spinningSprites = [];
    }

    this.isPlaying = false;
    this.hasStopped = false;
  }
}

export default ReelControllerV2;
